/**
 * The list's data, fetched THROUGH the registry so a reconnect refetches it.
 *
 * ┌─ ⛔ IT GOES THROUGH `subscribe()` AND NOT THROUGH `fetch` ──────────────────┐
 * │ A component calling `fetch` directly never enters the query store, so       │
 * │ `refetchAll()` cannot re-run it. It renders correctly on first load and     │
 * │ goes stale only after a reconnect nobody watched - **the missed-event       │
 * │ failure arriving through the fix for missed events.**                       │
 * │                                                                            │
 * │ That is silent by construction, so it has a check rather than a convention: │
 * │ `tests/web-query-registry.test.ts` scans `web/src` for direct network calls │
 * │ outside `data/` and fails on anything not exempted with a written reason.   │
 * │                                                                            │
 * │ ⚠️ THIS COMMENT DESCRIBES THE FORBIDDEN CALL RATHER THAN NAMING IT, AND     │
 * │ THAT IS THE THIRD TIME TODAY. The colour lint, the timezone lint and this   │
 * │ scan each flagged a comment explaining their own rule. A grep cannot tell   │
 * │ documentation from code, and exempting comments would let a real call hide  │
 * │ in one - so the checks are right and the prose has to work around them.     │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ THE KEY CARRIES THE VIEW. `conversations:default` and `conversations:unread`
 * are separate entries, so switching views does not overwrite the other's data
 * and a reconnect refetches whichever ones are actually mounted. One key for all
 * views would make the store's refcount lie about what is materialised.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { refetchKey, subscribe } from '../data/query-store.js'
import { type ConversationsPage, MAX_PAGES, fetchConversationPages } from '../data/inbox-api.js'
import type { ConversationListRow } from '../../../src/services/conversation-list.js'

export type UseConversations = {
  /** `null` until the first response. NOT the same as an empty array. */
  rows: ConversationListRow[] | null
  error: string | null
  /** Ask for one more page. A no-op at the end, or while one is in flight. */
  loadMore: () => void
  /**
   * ⛔ THREE STATES, NOT A BOOLEAN PAIR. `end` means the server said there is
   * nothing more; `capped` means WE stopped asking at `MAX_PAGES` and there IS
   * more. Collapsing them would tell an agent the inbox ends where our ceiling
   * is, which is a wrong answer delivered confidently - the same failure the
   * list's `null`-versus-`[]` split exists to prevent one layer down.
   */
  more: 'available' | 'loading' | 'end' | 'capped'
}

export function useConversations(view = 'default'): UseConversations {
  const [rows, setRows] = useState<ConversationListRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [cursor, setCursor] = useState<string | null>(null)
  const [loadingMore, setLoadingMore] = useState(false)
  /**
   * ⚠️ A REF, NOT STATE, AND DELIBERATELY OUTSIDE THE EFFECT'S DEPENDENCIES.
   * The fetcher handed to `subscribe` is captured once per view; it must read
   * the CURRENT page count when a reconnect re-runs it, not the count that was
   * current when the view mounted. State here would either re-subscribe on
   * every page turn - dropping and rebuilding the store entry - or close over a
   * stale number, which is how a reconnect would silently shrink the list.
   */
  const pagesRef = useRef(1)
  const key = `conversations:${view}`

  useEffect(() => {
    // ⛔ A VIEW CHANGE MUST CLEAR THE PREVIOUS VIEW'S ROWS. Without this the old
    // list stays on screen under the new view's heading until the fetch returns,
    // which is a wrong answer displayed confidently rather than a loading state.
    setRows(null)
    setError(null)
    // ⛔ THE PAGE COUNT RESETS WITH THE VIEW. Carrying it across would open the
    // new view already scrolled twenty pages deep, and pay for all of it.
    pagesRef.current = 1
    setCursor(null)
    setLoadingMore(false)

    const release = subscribe<ConversationsPage>(
      key,
      () => fetchConversationPages(view, pagesRef.current),
      (page) => {
        setRows(page.conversations)
        setCursor(page.cursor)
        setLoadingMore(false)
        setError(null)
      },
      /**
       * ⛔ THIS CALLBACK DID NOT EXIST UNTIL 2026-08-08, AND WITHOUT IT `error`
       * COULD ONLY EVER BE `null`.
       *
       * `setError` appeared twice in this file and both calls passed `null`.
       * `ConversationList` renders `Could not load conversations. {error}`
       * behind `error !== null`, styled, documented as one of three empty states
       * *"each decided rather than defaulted"* - and **unreachable**. A failed
       * fetch showed the LOADING state forever, which is precisely the
       * confusion those three states exist to prevent.
       *
       * ⚠️ Found by asking what could set the value, not by anything failing.
       * `captureReturnTo`'s shape: built, documented, and with no producer.
       */
      (err) => {
        // The message only. An Error's stack is noise to an agent and the
        // network detail is already in the browser's own console.
        setError(err instanceof Error ? err.message : 'Unknown error')
        // ⚠️ CLEARED HERE TOO. A failed page turn that left this true would
        // disable the control permanently, so the list would stop at the failure
        // and offer no way to try again.
        setLoadingMore(false)
      },
    )
    return release
  }, [view, key])

  const more: UseConversations['more'] =
    loadingMore ? 'loading' : cursor === null ? 'end' : pagesRef.current >= MAX_PAGES ? 'capped' : 'available'

  const loadMore = useCallback(() => {
    // ⚠️ GUARDED ON THE SAME VALUE THE UI RENDERS, so a double-click, a repeated
    // scroll event and a stray call all collapse into one request.
    if (more !== 'available') return
    pagesRef.current += 1
    setLoadingMore(true)
    void refetchKey(key)
  }, [key, more])

  return { rows, error, loadMore, more }
}
